import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Refresh, BatteryChargingFull } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { eventBus, EventTypes } from '@core/pubsub/EventBus';
import { dashboardService } from '@/services/dashboardService';
import { ShopId } from '@core/store/slices/apiSlice.ts';

// Apex Charts with React
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { BaseWidget } from '@/components/widgets/BaseWidget.tsx';

interface ChargingFastLanersData {
    totalFastLaners: number;
    chargingFastLaners: number;
}

interface FastLanerChargingProps {
    shopIdName: ShopId;
    heading: string;
    className?: string;
}

const FastLanerCharging: React.FC<FastLanerChargingProps> = ({ shopIdName, heading, className }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [chargingFastLanersData, setChargingFastLanersData] = useState<ChargingFastLanersData>({
        totalFastLaners: 0,
        chargingFastLaners: 0
    });

    const getIsChargingData = (response: any): ChargingFastLanersData => {
        let totalFastLaners = 0;
        let chargingFastLaners = 0;
        const groups = response?.data ?? [];

        groups.forEach((group: any) => {
            (group.trolleys ?? []).forEach((trolley: any) => {
                totalFastLaners += 1;
                if (trolley.isCharging) {
                    chargingFastLaners += 1;
                }
            });
        });

        return {
            totalFastLaners,
            chargingFastLaners
        };
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const fastLanerTrolleyData = await dashboardService.getFastLanerTrolleyData(shopIdName.fastLanerGroupIds);
            const chargingData = getIsChargingData(fastLanerTrolleyData);
            setChargingFastLanersData(chargingData);
        } catch (error) {
            console.error('Failed to load FastLaner charging:', error);
            setChargingFastLanersData({ totalFastLaners: 0, chargingFastLaners: 0 });
        } finally {
            setLoading(false);
        }
    }, [shopIdName.fastLanerGroupIds]);

    useEffect(() => {
        load();

        const unsubscribe = eventBus.on(EventTypes.WIDGETS_RELOAD, load);

        return unsubscribe;
    }, [load]);

    const menuActions = [
        {
            text: t('refresh'),
            icon: <Refresh fontSize="small" />,
            onClick: load
        }
    ];

    // Calculate percentage and prepare chart data
    const percentage = chargingFastLanersData.totalFastLaners
        ? (chargingFastLanersData.chargingFastLaners / chargingFastLanersData.totalFastLaners) * 100
        : 0;

    const series = [percentage];

    const options: ApexOptions = {
        chart: {
            type: 'radialBar',
            height: 300,
            toolbar: {
                show: false
            },
            background: 'transparent',
            fontFamily: 'Wanzl_Etelka Light, Roboto, sans-serif'
        },
        plotOptions: {
            radialBar: {
                startAngle: -100,
                endAngle: 100,
                track: {
                    startAngle: -100,
                    endAngle: 100
                },
                dataLabels: {
                    value: {
                        fontSize: '25px',
                        offsetY: -10,
                        formatter: () => {
                            const charging = chargingFastLanersData.chargingFastLaners || '-';
                            const total = chargingFastLanersData.totalFastLaners || '-';
                            return `${charging} / ${total}`;
                        }
                    }
                }
            }
        },
        colors: ['#2196F3'],
        labels: [''],
        theme: {
            mode: 'light'
        }
    };

    return (
        <BaseWidget
            heading={heading}
            icon={<BatteryChargingFull />}
            className={className}
            customActions={menuActions}
            loading={loading}
        >
            <div>
                <Chart options={options} series={series} type="radialBar" height={300} />
            </div>
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    {t('chargingFastLaners')}
                </Typography>
            </Box>
        </BaseWidget>
    );
};

export default FastLanerCharging;
